// src/hooks/useAccessibleModules.js
import { useState, useEffect } from 'react';
import settingsService from '../services/SettingsService';
import sessionService from '../services/SessionService';
import userService from '../services/UserService';
import licenseService from '../services/LicenseService';
import entityService from '../services/EntityService';

const getAccessibleModuleIds = () => {
  const globalEnabled = settingsService.getEnabledModules();
  const session = sessionService.getSession();
  const activeEntityId = entityService.getActiveEntityId();
  let filtered = globalEnabled;
  if (session?.username) {
    const allowedIds = userService.getUserAllowedModuleIds(session.username);
    if (allowedIds) filtered = filtered.filter(m => allowedIds.includes(m.id));
  }
  return filtered
    .filter(m => licenseService.isLicensed(activeEntityId, m.id))
    .map(m => m.id);
};

const getUnlicensedModuleIds = () => {
  const globalEnabled = settingsService.getEnabledModules();
  const session = sessionService.getSession();
  const activeEntityId = entityService.getActiveEntityId();
  let filtered = globalEnabled;
  if (session?.username) {
    const allowedIds = userService.getUserAllowedModuleIds(session.username);
    if (allowedIds) filtered = filtered.filter(m => allowedIds.includes(m.id));
  }
  return filtered
    .filter(m => !licenseService.isLicensed(activeEntityId, m.id))
    .map(m => m.id);
};

// Returns the list of module IDs accessible to the current user:
// globally enabled + user permissions + licensed for the active entity.
const useAccessibleModules = () => {
  const [moduleIds, setModuleIds] = useState(getAccessibleModuleIds);

  useEffect(() => {
    const handleChanged = () => setModuleIds(getAccessibleModuleIds());
    window.addEventListener('rms_modules_changed', handleChanged);
    window.addEventListener('rms_licenses_changed', handleChanged);
    window.addEventListener('rms_active_entity_changed', handleChanged);
    const unsubUser = userService.subscribe(handleChanged);
    const unsubSettings = settingsService.subscribe(handleChanged);
    const unsubLicense = licenseService.subscribe(handleChanged);
    const unsubEntity = entityService.subscribe(handleChanged);
    return () => {
      window.removeEventListener('rms_modules_changed', handleChanged);
      window.removeEventListener('rms_licenses_changed', handleChanged);
      window.removeEventListener('rms_active_entity_changed', handleChanged);
      unsubUser();
      unsubSettings();
      unsubLicense();
      unsubEntity();
    };
  }, []);

  return moduleIds;
};

// Returns module IDs that are enabled + user-accessible but blocked by missing license.
export const useUnlicensedModules = () => {
  const [moduleIds, setModuleIds] = useState(getUnlicensedModuleIds);

  useEffect(() => {
    const handleChanged = () => setModuleIds(getUnlicensedModuleIds());
    window.addEventListener('rms_modules_changed', handleChanged);
    window.addEventListener('rms_licenses_changed', handleChanged);
    window.addEventListener('rms_active_entity_changed', handleChanged);
    const unsubUser = userService.subscribe(handleChanged);
    const unsubSettings = settingsService.subscribe(handleChanged);
    const unsubLicense = licenseService.subscribe(handleChanged);
    const unsubEntity = entityService.subscribe(handleChanged);
    return () => {
      window.removeEventListener('rms_modules_changed', handleChanged);
      window.removeEventListener('rms_licenses_changed', handleChanged);
      window.removeEventListener('rms_active_entity_changed', handleChanged);
      unsubUser();
      unsubSettings();
      unsubLicense();
      unsubEntity();
    };
  }, []);

  return moduleIds;
};

export default useAccessibleModules;
